import { WorkflowOutput, AgentConfigOutput } from "@webuild-ai/admin/models/components";
import { WorkflowConfig, AgentConfig } from "@webuild-ai/agent-platform/models/components";

import { Workflow } from "@/stores/WorkflowStore";

// Helper function to determine if a workflow is specialized (not the default chat workflow)
export const isSpecializedWorkflow = (workflow: Workflow | null): boolean => {
  return workflow?.id !== "chat_agent";
};

/**
 * Extracts the default prompt value from a workflow's input schema.
 * Returns null if no default value is defined.
 *
 * Schema example:
 * {
 *   "input": {
 *     "prompt": {
 *       "type": "string",
 *       "description": "Click Send to generate your report",
 *       "value": "Generate Report"  // ← This is the default value
 *     }
 *   }
 * }
 */
export const getWorkflowDefaultPrompt = (workflow: Workflow | null): string | null => {
  if (!workflow?.input) return null;

  // Check for prompt.value in the input schema
  const promptInput = workflow.input?.prompt;

  if (promptInput && typeof promptInput === "object" && "value" in promptInput) {
    return promptInput.value || null;
  }

  return null;
};

const convertAgentConfig = (adminAgent: AgentConfigOutput): AgentConfig => ({
  type: adminAgent.type,
  // Cast parameters - admin uses Array<any>, agent-platform uses Array<string>
  parameters: adminAgent.parameters as { [k: string]: string | number | Array<string> },
  tools: adminAgent.tools,
  triggers: adminAgent.triggers,
  userInteraction: adminAgent.userInteraction,
});

/**
 * Converts WorkflowOutput from admin API to WorkflowConfig for agent-platform API.
 *
 * This converter provides safe default values for the required fields.
 */
export const convertWorkflowOutputToConfig = (workflow: WorkflowOutput): WorkflowConfig => {
  // Convert agents
  const agents = Object.entries(workflow.agents).reduce(
    (acc, [key, agent]) => {
      acc[key] = convertAgentConfig(agent);

      return acc;
    },
    {} as { [k: string]: AgentConfig },
  );

  return {
    name: workflow.name,
    id: workflow.id,
    description: workflow.description,
    chatManagerPrompt: workflow.chatManagerPrompt,
    scheduled: workflow.scheduled,
    input: workflow.input,
    agents,
  };
};
